import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import JSZip from "jszip";
import { auth, db } from "../firebase/config";
import AIService from "./AIService";
import {
  ANDROID_ICON_SIZES,
  CAPACITOR_ICON_SIZES,
  CreateIconDTO,
  GeneratedIcon,
  GeneratedIconVariant,
  GooglePlayIconPublishResponse,
  GooglePlayIconValidationResponse,
  IconCropSettings,
  IconPlatform,
  iOS_ICON_SIZES,
} from "../types/icon";
import { buildAppContextLines } from "../utils/appContextPrompt";

const ICONS_COLLECTION = "appIcons";
const PUBLISH_GOOGLE_PLAY_ICON_ENDPOINT =
  process.env.REACT_APP_PUBLISH_GOOGLE_PLAY_ICON_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/publishGooglePlayIcon";
const VALIDATE_GOOGLE_PLAY_ICON_ENDPOINT =
  process.env.REACT_APP_VALIDATE_GOOGLE_PLAY_ICON_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/validateGooglePlayIcon";

class IconService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  private generateIconId(): string {
    return `icon_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  private convertTimestampToDate(timestamp: unknown): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (typeof timestamp === "string" || typeof timestamp === "number") {
      return new Date(timestamp);
    }
    return new Date();
  }

  async getIconsByAppId(
    appId: string,
    userId: string,
  ): Promise<GeneratedIcon[]> {
    const iconsRef = collection(db, ICONS_COLLECTION);
    const q = query(
      iconsRef,
      where("appId", "==", appId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((iconDoc) => {
      const data = iconDoc.data();
      return {
        iconId: data.iconId,
        appId: data.appId,
        userId: data.userId,
        baseImageUrl: data.baseImageUrl,
        sourceAssetId: data.sourceAssetId,
        sourceStoragePath: data.sourceStoragePath,
        prompt: data.prompt,
        style: data.style,
        mood: data.mood,
        colorScheme: data.colorScheme,
        platform: data.platform,
        metadata: {
          generatedAt: this.convertTimestampToDate(data.metadata?.generatedAt),
          aiModel: data.metadata?.aiModel || "",
          provider: data.metadata?.provider || "gemini",
        },
        rating: data.rating,
        approved: data.approved,
      } as GeneratedIcon;
    });
  }

  async generateIcon(
    userId: string,
    iconRequest: CreateIconDTO,
  ): Promise<GeneratedIcon[]> {
    const prompt = this.buildPrompt(iconRequest);
    const aiResult = await AIService.generateImage(prompt, {
      appId: iconRequest.appId,
      assetCategory: "app-icon",
      model: iconRequest.model,
      aspectRatio: "1:1",
      n: 1,
    });

    const createdIcons: GeneratedIcon[] = [];

    for (const image of aiResult.images) {
      const iconId = this.generateIconId();
      const newIcon = {
        iconId,
        appId: iconRequest.appId,
        userId,
        baseImageUrl: image.downloadUrl,
        sourceAssetId: image.assetId,
        sourceStoragePath: image.storagePath,
        prompt,
        style: iconRequest.style,
        mood: iconRequest.mood,
        colorScheme: iconRequest.colorScheme,
        platform: iconRequest.platform,
        metadata: {
          generatedAt: serverTimestamp(),
          aiModel: aiResult.model,
          provider: aiResult.provider,
        },
        rating: 0,
        approved: false,
      };

      await addDoc(collection(db, ICONS_COLLECTION), newIcon);

      createdIcons.push({
        ...newIcon,
        metadata: {
          generatedAt: new Date(),
          aiModel: aiResult.model,
          provider: aiResult.provider,
        },
      } as GeneratedIcon);
    }

    return createdIcons;
  }

  async updateIconRating(
    iconId: string,
    userId: string,
    rating: number,
  ): Promise<void> {
    const q = query(
      collection(db, ICONS_COLLECTION),
      where("iconId", "==", iconId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Icon not found");
    }

    await updateDoc(querySnapshot.docs[0].ref, { rating });
  }

  async approveIcon(iconId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, ICONS_COLLECTION),
      where("iconId", "==", iconId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Icon not found");
    }

    await updateDoc(querySnapshot.docs[0].ref, { approved: true });
  }

  async deleteIcon(iconId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, ICONS_COLLECTION),
      where("iconId", "==", iconId),
      where("userId", "==", userId),
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Icon not found");
    }

    await deleteDoc(querySnapshot.docs[0].ref);
  }

  getIconSizesForPlatform(platform: IconPlatform) {
    if (platform === "ios") return [...iOS_ICON_SIZES, ...CAPACITOR_ICON_SIZES];
    if (platform === "android") return [...ANDROID_ICON_SIZES, ...CAPACITOR_ICON_SIZES];
    return [...iOS_ICON_SIZES, ...ANDROID_ICON_SIZES, ...CAPACITOR_ICON_SIZES];
  }

  async generateIconVariants(
    icon: GeneratedIcon,
    crop: IconCropSettings,
    platform: IconPlatform,
  ): Promise<GeneratedIconVariant[]> {
    if (!icon.sourceStoragePath?.trim()) {
      throw new Error(
        "Selected icon does not have a source storage path for backend processing.",
      );
    }

    const response = await AIService.generateIconVariants({
      appId: icon.appId,
      sourceStoragePath: icon.sourceStoragePath,
      platform,
      crop,
    });

    return (response.variants || []).map((variant) => ({
      platform: variant.platform,
      size: variant.size,
      name: variant.name,
      fileName: variant.fileName,
      dataUrl: `data:${variant.mimeType || "image/png"};base64,${
        variant.base64
      }`,
    }));
  }

  async downloadVariants(
    variants: GeneratedIconVariant[],
    appName: string,
  ): Promise<void> {
    const zip = new JSZip();
    const safeAppName = appName.replace(/[^a-zA-Z0-9-_]/g, "_");

    for (const variant of variants) {
      const base64 = variant.dataUrl.split(",")[1];
      if (!base64) continue;
      const path =
        variant.platform === "capacitor"
          ? variant.fileName
          : `${variant.platform}/${variant.fileName}`;
      zip.file(path, base64, { base64: true });
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeAppName}-icon-assets.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async validateGooglePlayIcon(params: {
    appId: string;
    iconId: string;
  }): Promise<GooglePlayIconValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayIconValidationResponse>(
        VALIDATE_GOOGLE_PLAY_ICON_ENDPOINT,
        {
          appId: params.appId,
          iconId: params.iconId,
        },
      );

    return (
      responseData || {
        success: false,
        appId: params.appId,
        iconId: params.iconId,
        packageName: "",
        locale: "",
        playLocale: "",
        status: "error",
        current: {
          count: 0,
          images: [],
        },
        proposed: {
          iconUrl: "",
        },
        validationIssues: [],
      }
    );
  }

  async publishGooglePlayIcon(params: {
    appId: string;
    iconId: string;
  }): Promise<GooglePlayIconPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayIconPublishResponse>(
        PUBLISH_GOOGLE_PLAY_ICON_ENDPOINT,
        {
          appId: params.appId,
          iconId: params.iconId,
        },
      );

    return (
      responseData || {
        success: false,
        appId: params.appId,
        iconId: params.iconId,
        packageName: "",
        locale: "",
        playLocale: "",
        commitStatus: "not_committed",
      }
    );
  }

  private buildPrompt(request: CreateIconDTO): string {
    const hasPrimary = Boolean(request.colorScheme.primary?.trim());
    const hasSecondary = Boolean(request.colorScheme.secondary?.trim());
    const colorLine =
      hasPrimary || hasSecondary
        ? `Colors: ${[
            hasPrimary ? `primary ${request.colorScheme.primary}` : "",
            hasSecondary ? `secondary ${request.colorScheme.secondary}` : "",
            request.colorScheme.accent?.trim()
              ? `accent ${request.colorScheme.accent}`
              : "",
          ]
            .filter(Boolean)
            .join(", ")}.`
        : "Colors: no fixed palette specified.";

    const lines = [
      `Generate a premium app icon for "${request.appName}".`,
      request.styleMode === "ai"
        ? `AI style direction: ${
            request.aiStylePrompt ||
            "Use modern, distinctive icon art direction."
          }`
        : `Style: ${request.style}. Mood: ${request.mood}.`,
      request.styleMode === "ai"
        ? "Priority: follow the AI style direction above as the primary creative brief and preserve its sectioned structure."
        : "",
      colorLine,
      `Target platform output preference: ${request.platform}.`,
      `Primary design target platform: ${request.targetPlatform || "ios"}.`,
      ...buildAppContextLines({
        name: request.appName,
        description: request.appDescription,
        category: request.appCategory,
        platform: request.appPlatform,
        storeUrl: request.appStoreUrl,
        androidStoreUrl: request.androidStoreUrl,
        iosStoreUrl: request.iosStoreUrl,
        researchContext: request.researchContext,
      }),
      `Keywords: ${request.keywords.join(", ") || "N/A"}.`,
      `Include symbols: ${request.symbols?.join(", ") || "N/A"}.`,
      `Avoid elements: ${request.avoidElements?.join(", ") || "N/A"}.`,
      request.styleMode === "ai"
        ? `AI selected style/mood anchor: style=${
            request.aiSuggestedStyle || "minimalist"
          }, mood=${request.aiSuggestedMood || "professional"}.`
        : "",
      "Design quality framework: prioritize one dominant focal subject and one supporting shape at most; remove non-essential details.",
      "Shape constraints: generate a full-bleed square icon image with hard square corners; do not render rounded corners, masks, device frames, or baked radius effects.",
      "Recognition constraints: enforce strong silhouette, clear semantic metaphor linked to app purpose, and immediate recognizability at small sizes.",
      "Small-size constraints: must pass 60px legibility and squint-test readability; avoid fine micro-lines, tiny ornaments, and dense texture.",
      "Color constraints: use a disciplined 2-3 color palette with high foreground/background separation and clear visual hierarchy.",
      "Differentiation constraints: avoid generic store-icon tropes in this category; keep the concept distinctive while preserving clarity and trust.",
      "Constraints: single centered icon mark, no text, no watermark, clean edges, high contrast, export-safe square composition, and accessibility-safe foreground/background contrast.",
      "Quality constraints: crisp details, no blur haze, no compression artifacts, no malformed symbols, no broken geometry, and no visual clutter.",
      "Framing constraints: keep the full icon mark comfortably inside the frame with generous safe margins; avoid edge clipping or tangent touches.",
      "Anatomy/shape constraints: avoid distorted or unnatural forms (including warped hands/fingers or impossible body parts if human-like elements are used).",
      "Layout-safe area: place all core icon content within the center 70% of the canvas so crops and downscaled exports remain clear.",
    ];
    return lines.join("\n");
  }
}

export default new IconService();
